import type { ReactNode } from "react";
import { generatePath, useParams } from "react-router";
import { styled } from "@pigment-css/react";
import { ChevronLeft, CrossLarge } from "@acto/web-common/components";
import { useTranslations } from "@acto/web-common/locales";
import ActoRoutes from "@acto/taskpane/core/router/routes.config";
import { useBackNavigation } from "@acto/taskpane/field-sales-2/navigation";
import { useShellDismiss } from "@acto/taskpane/field-sales-2/shell";
import { useDirectionalNavigate } from "@acto/taskpane/field-sales-2/utils/viewTransitions";
import { HeaderIconButton } from "@acto/taskpane/field-sales-2/components/primitives/HeaderIconButton";
import { shellTopBarHeight } from "@acto/taskpane/field-sales-2/styles/shellTopBarHeight";

const ShellNav = styled("header")(({ theme }) => ({
  position: "sticky",
  top: 0,
  // Above the breakdown toggle's trigger (CalendarSelectMenu, zIndex 6) so the close button stays
  // on top as the list header scrolls up under it.
  zIndex: 7,
  width: "100%",
  padding: `${theme.fieldSalesTwo.spacing[72]} ${theme.fieldSalesTwo.spacing[16]} ${theme.fieldSalesTwo.spacing[32]}`,
  // Fixed so that taller slot content overflows into the padding instead of growing the bar and
  // shifting the content beneath it.
  height: shellTopBarHeight(theme),
  background: `linear-gradient(to bottom, ${theme.fieldSalesTwo.colors.background} 34.6%, rgba(240,238,237,0) 100%)`,
  boxSizing: "border-box",
  display: "grid",
  gridTemplateColumns: "1fr auto 1fr",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[8],
  // leadingGrow: the default trailing 1fr would cap the leading slot at ~50% of the bar.
  '&[data-leading-grow="true"]': {
    gridTemplateColumns: "minmax(0, 1fr) auto auto",
    "& [data-nav-start]": { justifySelf: "stretch" },
  },
}));

const NavStart = styled("div")(({ theme }) => ({
  gridColumn: 1,
  justifySelf: "start",
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[8],
  minWidth: 0,
}));

const NavEnd = styled("div")({ gridColumn: 3, justifySelf: "end", display: "flex", alignItems: "center" });

// Centered node slot, sharing gridColumn 2 with the string title. `minWidth: 0` lets a wide child
// shrink instead of overflowing into the side slots.
const NavCenter = styled("div")({
  gridColumn: 2,
  justifySelf: "center",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 0,
  maxWidth: "100%",
});

const ShellNavTitle = styled("span")(({ theme }) => ({
  gridColumn: 2,
  justifySelf: "center",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[520],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.subtle,
  color: theme.fieldSalesTwo.colors.neutral[900],
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: "100%",
}));

interface ShellTopBarProps {
  leading?: ReactNode;
  title?: string;
  /** Centered node in the middle column. Takes precedence over `title`. To center it against the
   *  bar rather than the leading slot, leave `leadingGrow` off. */
  center?: ReactNode;
  trailing?: ReactNode;
  /** Let the leading slot fill the bar (trailing shrinks to its content) so a wide leading pill
   *  can grow and truncate at the edge. Off by default. */
  leadingGrow?: boolean;
}

export function ShellTopBar({ leading, title, center, trailing, leadingGrow }: ShellTopBarProps) {
  return (
    <ShellNav data-leading-grow={leadingGrow ? "true" : undefined}>
      <NavStart data-nav-start>{leading}</NavStart>
      {center ? <NavCenter>{center}</NavCenter> : title ? <ShellNavTitle>{title}</ShellNavTitle> : null}
      <NavEnd>{trailing}</NavEnd>
    </ShellNav>
  );
}

export function ShellBackButton() {
  const { id } = useParams<{ id: string }>();
  const t = useTranslations("fieldSalesTwo.customerNav");
  const handleBack = useBackNavigation(id ? generatePath(ActoRoutes.CUSTOMER_DETAILS, { id }) : ActoRoutes.HOME);

  return (
    <HeaderIconButton type="button" aria-label={t("back")} onClick={handleBack}>
      <ChevronLeft />
    </HeaderIconButton>
  );
}

interface ShellCloseButtonProps {
  /** Where the X navigates. Defaults to Home (the customer root); first-layer subpages pass the
   *  customer-overview path so the X closes back to the customer instead of all the way Home. */
  to?: string;
}

export function ShellCloseButton({ to }: ShellCloseButtonProps = {}) {
  const navigate = useDirectionalNavigate();
  const t = useTranslations("fieldSalesTwo.customerNav");
  const { requestDismiss, hasOverlay } = useShellDismiss();

  return (
    <HeaderIconButton
      type="button"
      aria-label={hasOverlay ? t("closeOverlay") : t("close")}
      onClick={() => {
        if (!requestDismiss()) navigate(to ?? ActoRoutes.HOME, { direction: "pop" });
      }}
    >
      <CrossLarge />
    </HeaderIconButton>
  );
}
